import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import BookingLinkModal from './BookingLinkModal';
import {
  LayoutDashboard, FileText, Users, Package, Wrench, DollarSign,
  BarChart3, Settings, LogOut, Menu, X, ChevronRight, ChevronDown, Shield, CreditCard, Clock,
  UserCog, MessageSquare, MessageCircle, CalendarDays, Palette, Wallet
} from 'lucide-react';

const NAV = [
  { path: '/', label: 'Orçamentos', icon: FileText },
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/clients', label: 'Clientes', icon: Users },
  {
    path: '/agenda', label: 'Agenda', icon: CalendarDays,
    children: [
      { label: 'Link de agendamento', navState: { openModal: 'link' } },
      { label: 'Configurar horários', navState: { openModal: 'config' } },
    ]
  },
  { path: '/whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { path: '/temas', label: 'Temas', icon: Palette },
  { path: '/products', label: 'Produtos', icon: Package },
  { path: '/services', label: 'Serviços', icon: Wrench },
  { path: '/employees', label: 'Funcionários', icon: UserCog },
  {
    label: 'Financeiro', icon: Wallet,
    children: [
      { path: '/receivables', label: 'Contas a Receber' },
      { path: '/financeiro/despesas', label: 'Despesas Mensais' },
      { path: '/financeiro/caixa', label: 'Fluxo de Caixa' },
    ]
  },
  { path: '/reports', label: 'Relatórios', icon: BarChart3 },
  { path: '/settings', label: 'Configurações', icon: Settings },
  { path: '/suggestions', label: 'Sugestões', icon: MessageSquare },
];

export default function Layout() {
  const { user, logout, trialDaysLeft, isTrialExpired } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Onboarding: enquanto a empresa não configurar o slug do link de agendamento,
  // o modal reabre em toda entrada no sistema — evita mandar o cliente um link em código.
  const [bookingLink, setBookingLink] = useState(null);
  const [bookingSlug, setBookingSlug] = useState(null);
  const [bookingSlugInput, setBookingSlugInput] = useState('');
  const [savingBookingSlug, setSavingBookingSlug] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);

  useEffect(() => {
    if (!user || user.is_admin) return;
    api.post('/agenda/booking-link').then(res => {
      setBookingLink(res.data.link);
      setBookingSlug(res.data.slug || null);
      setBookingSlugInput(res.data.slug || '');
      if (!res.data.slug) setShowBookingModal(true);
    }).catch(() => {});
  }, [user?.id, user?.is_admin]);

  const salvarBookingSlug = async () => {
    setSavingBookingSlug(true);
    try {
      const res = await api.put('/agenda/slug', { slug: bookingSlugInput });
      setBookingSlug(res.data.slug);
      setBookingSlugInput(res.data.slug);
      setBookingLink(res.data.link);
      toast.success('Endereço do link salvo');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar endereço');
    } finally { setSavingBookingSlug(false); }
  };

  const copiarBookingLink = () => {
    navigator.clipboard.writeText(bookingLink);
    toast.success('Link copiado');
  };

  const daysLeft = trialDaysLeft();
  const expired = isTrialExpired();

  const NavItem = ({ item }) => {
    const active = location.pathname === item.path ||
      (item.path !== '/' && location.pathname.startsWith(item.path));
    const Icon = item.icon;
    return (
      <button
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        onClick={() => { navigate(item.path); setSidebarOpen(false); }}
        className={`sidebar-item w-full text-left ${active ? 'active' : ''}`}
      >
        <Icon size={18} />
        <span>{item.label}</span>
        {active && <ChevronRight size={14} className="ml-auto text-orange-400" />}
      </button>
    );
  };

  const NavGroup = ({ item }) => {
    const childActive = item.children.some(c => c.path && (location.pathname === c.path || location.pathname.startsWith(c.path)));
    const selfActive = item.path && (location.pathname === item.path || location.pathname.startsWith(item.path));
    const active = childActive || selfActive;
    const [open, setOpen] = useState(active);
    const Icon = item.icon;
    return (
      <div>
        <div className={`flex items-center rounded-lg ${active ? 'text-orange-400 bg-orange-500/10 border border-orange-500/20' : 'text-zinc-400'}`}>
          <button
            onClick={() => { if (item.path) { navigate(item.path); setSidebarOpen(false); } else { setOpen(o => !o); } }}
            className="flex-1 flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-left rounded-lg hover:text-white hover:bg-white/5 transition-colors duration-150 cursor-pointer"
          >
            <Icon size={18} />
            <span>{item.label}</span>
          </button>
          <button onClick={() => setOpen(o => !o)} className="pr-3 pl-2 py-2.5 text-zinc-500 hover:text-white rounded-r-lg hover:bg-white/5 transition-colors duration-150 cursor-pointer">
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        </div>
        {open && (
          <div className="ml-6 space-y-0.5 mt-0.5">
            {item.children.map(c => {
              const active = c.path && (location.pathname === c.path || location.pathname.startsWith(c.path));
              return (
                <button
                  key={c.path || c.label}
                  data-testid={`nav-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => { navigate(c.path || item.path, { state: c.navState }); setSidebarOpen(false); }}
                  className={`sidebar-item w-full text-left text-sm ${active ? 'active' : ''}`}
                >
                  <span>{c.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-20 bg-black/60 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-30 w-64 flex flex-col
        bg-zinc-900/95 border-r border-zinc-800 backdrop-blur-xl
        transform transition-transform duration-200 lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <div className="flex items-center gap-2.5">
            <img src="/icon-192.png" alt="OrcFácil" className="w-8 h-8 rounded-lg object-cover" />
            <span className="font-heading font-bold text-lg text-white">OrcFácil</span>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-zinc-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        {/* Trial banner */}
        {user && !user.is_admin && user.subscription_status !== 'active' && (
          <div
            className={`mx-3 mt-3 px-3 py-2.5 rounded-lg text-xs cursor-pointer ${
              expired ? 'bg-red-500/20 border border-red-500/30 text-red-400' :
              daysLeft <= 3 ? 'bg-orange-500/20 border border-orange-500/30 text-orange-400' :
              'bg-zinc-800 border border-zinc-700 text-zinc-400'
            }`}
            onClick={() => navigate('/subscribe')}
          >
            <div className="flex items-center gap-1.5 font-medium">
              <Clock size={12} />
              {expired ? 'Trial expirado' : `Trial: ${daysLeft} dias restantes`}
            </div>
            <div className="mt-0.5 text-[11px] opacity-70">Clique para assinar</div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto mt-2">
          {NAV.map(item => item.children
            ? <NavGroup key={item.label} item={item} />
            : <NavItem key={item.path} item={item} />
          )}
          {user?.is_admin && (
            <NavItem item={{ path: '/admin', label: 'Admin', icon: Shield }} />
          )}
        </nav>

        {/* User footer */}
        <div className="p-3 border-t border-zinc-800">
          <div className="flex items-center gap-3 px-3 py-2 mb-1">
            <div className="w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center text-orange-400 font-bold text-sm">
              {user?.nome?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white truncate">{user?.nome}</div>
              <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
            </div>
          </div>
          {!user?.is_admin && user?.subscription_status !== 'active' && (
            <button
              onClick={() => navigate('/subscribe')}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-orange-400 hover:bg-orange-500/10 rounded-lg transition-colors"
            >
              <CreditCard size={14} />
              <span>Assinar plano Pro</span>
            </button>
          )}
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
          >
            <LogOut size={16} />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header className="h-14 flex items-center gap-4 px-4 border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-xl shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-zinc-400 hover:text-white p-1"
            data-testid="menu-btn"
          >
            <Menu size={22} />
          </button>
          {location.pathname.startsWith('/agenda') && (
            <span className="font-heading font-semibold text-white">Agenda</span>
          )}
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500 hidden sm:block">{user?.empresa}</span>
            <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              user?.is_admin ? 'bg-purple-500/20 text-purple-400' :
              user?.subscription_status === 'active' ? 'bg-green-500/20 text-green-400' :
              'bg-zinc-700 text-zinc-400'
            }`}>
              {user?.is_admin ? 'Admin' : user?.subscription_status === 'active' ? 'Pro' : 'Trial'}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      <BookingLinkModal
        open={showBookingModal}
        mandatory
        link={bookingLink}
        slug={bookingSlug}
        slugInput={bookingSlugInput}
        setSlugInput={setBookingSlugInput}
        savingSlug={savingBookingSlug}
        onSave={salvarBookingSlug}
        onCopy={copiarBookingLink}
        onClose={() => setShowBookingModal(false)}
      />
    </div>
  );
}
